import React, { useState, useEffect, useCallback } from 'react';
import { getPortfolioIncidents } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  Clock,
  Users,
  BarChart3,
  Calendar,
  Eye,
  Globe,
  AlertCircle,
  CheckCircle,
  Loader2,
  Activity,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const SEVERITY_COLORS = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#7c2d12',
};

const STATUS_COLORS = {
  open: '#ef4444',
  investigating: '#f59e0b',
  resolved: '#10b981',
  closed: '#6b7280',
};

export default function PortfolioIncidents() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPortfolioIncidents({ days: parseInt(period) });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching portfolio incidents:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const severityData = data?.severity_distribution ? [
    { name: 'Low', value: data.severity_distribution.low, color: SEVERITY_COLORS.low },
    { name: 'Medium', value: data.severity_distribution.medium, color: SEVERITY_COLORS.medium },
    { name: 'High', value: data.severity_distribution.high, color: SEVERITY_COLORS.high },
    { name: 'Critical', value: data.severity_distribution.critical, color: SEVERITY_COLORS.critical },
  ].filter(d => d.value > 0) : [];

  const statusData = data?.status_distribution ? Object.entries(data.status_distribution).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: STATUS_COLORS[name] || '#6b7280',
  })) : [];

  const categoryData = data?.top_categories?.map(cat => ({
    category: cat.category,
    count: cat.count,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Portfolio Incident Analytics</h2>
            <p className="text-sm text-muted-foreground">Aggregate incident data across all organizations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <Activity className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Incidents</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                  {data?.summary?.total_incidents || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {data?.summary?.per_org_average || 0} per organization avg
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Incidents</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-yellow-500">
                  {data?.summary?.open_incidents || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Requiring attention</p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Elevated Risk</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1 text-red-500">
                  {data?.severity_concentration?.elevated_risk || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {data?.severity_concentration?.elevated_risk_pct || 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Repeat Offenders</p>
                <p className="text-3xl font-bold font-['Barlow_Condensed'] mt-1">
                  {data?.summary?.repeat_incident_orgs || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-orange-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Orgs with 3+ incidents</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Severity Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Severity Distribution</CardTitle>
            <CardDescription>Incidents grouped by severity level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={severityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {severityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status Distribution</CardTitle>
            <CardDescription>Current status of all incidents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Top Incident Categories
          </CardTitle>
          <CardDescription>Most common incident types across the portfolio</CardDescription>
        </CardHeader>
        <CardContent>
          {categoryData.length > 0 ? (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#93c5fd" radius={[4, 4, 0, 0]} name="Incidents" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No category data available</p>
          )}
        </CardContent>
      </Card>

      {/* Risk Signals */}
      {data?.summary?.repeat_incident_orgs > 0 && (
        <Alert className="bg-orange-500/10 border-orange-500/30">
          <AlertTriangle className="w-4 h-4 text-orange-600" />
          <AlertDescription className="text-orange-600">
            <strong>Risk Signal:</strong> {data.summary.repeat_incident_orgs} organizations have 3 or more incidents. 
            Consider targeted intervention and safety protocol reviews.
          </AlertDescription>
        </Alert>
      )}

      {/* Drill-down Prompt */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Eye className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-600">
          <strong>Drill-Down Available:</strong> Switch to Organization View and select an organization to view their specific incidents, create new reports, and run AI analysis.
        </AlertDescription>
      </Alert>
    </div>
  );
}
